// FreeEvaluation — behind the "FREE Resume Evaluation" banner.
// Two options: (1) free expert resume evaluation WITH resume attached;
// (2) free 1-on-1 strategy call request. Delivered to Marcus's inbox via the
// shared attachment-capable email endpoint (a public, rate-limited service).
import { useState } from "react";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";
import { FileText, PhoneCall, CheckCircle2, Loader2, Upload, Send } from "lucide-react";

// TailorWiz's OWN email endpoint (gxat project) — delivers to marcus@tailorwiz.com.
// Never route TailorWiz leads through the JobIntel project.
const MAIL_ENDPOINT = "https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/send-contact-message";
const MAIL_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I";
// Live demo / strategy call booking — Marcus's Calendly.
const CALENDLY_URL = "https://calendly.com/marcusbhall/tailorwiz-live-demo";

type Tab = "evaluation" | "call";

const inputCls =
  "w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0B1C3F] focus:bg-white transition-colors";

export default function FreeEvaluation() {
  const [tab, setTab] = useState<Tab>("evaluation");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Tab | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onFile(f: File | null) {
    setError(null);
    if (!f) { setFile(null); return; }
    if (!/\.(pdf|docx?|txt|rtf)$/i.test(f.name)) { setError("Please attach a PDF, Word, or text file."); return; }
    if (f.size > 5 * 1024 * 1024) { setError("Please keep your resume under 5MB."); return; }
    setFile(f);
  }

  function fileToBase64(f: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
      r.onerror = reject;
      r.readAsDataURL(f);
    });
  }

  async function submit() {
    setError(null);
    if (!name.trim() || !email.trim()) { setError("Please enter your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email."); return; }
    if (!file) { setError("Please attach your resume — that's what we evaluate!"); return; }
    setSending(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim(),
        subject: "[TailorWiz] FREE Resume Evaluation request",
        message: notes.trim(),
        form_type: "resume-evaluation",
        phone: phone.trim() || undefined,
        target_role: targetRole.trim() || undefined,
      };
      if (file) {
        payload.resume_filename = file.name;
        payload.resume_base64 = await fileToBase64(file);
      }
      const res = await fetch(MAIL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: MAIL_ANON, Authorization: `Bearer ${MAIL_ANON}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error ?? "Couldn't send — try again.");
      setSent(tab);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingNav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-white">
          <div className="bg-gradient-to-b from-[#0B1C3F] to-[#0E2350] px-8 py-7 text-center">
            <h1 className="text-white font-extrabold text-[26px] tracking-tight">Free Expert Help — On Us</h1>
            <p className="text-slate-300 text-[15px] mt-1">
              Pick what you need. A real career expert responds — usually the same day.
            </p>
          </div>

          {sent ? (
            <div className="p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-[20px] font-extrabold text-slate-900 mb-1">
                {sent === "evaluation" ? "Your resume is on its way!" : "Call request received!"}
              </p>
              <p className="text-[14px] text-slate-500 max-w-md mx-auto">
                {sent === "evaluation"
                  ? "One of our experts will personally review it and reply to " + email + " with your evaluation — usually the same day."
                  : "We'll reply to " + email + " to lock in a time that works for you — usually the same day."}
              </p>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => { setTab("evaluation"); setError(null); }}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${
                    tab === "evaluation" ? "border-[#C9A84C] bg-[#C9A84C]/10 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <FileText className={`w-5 h-5 mb-2 ${tab === "evaluation" ? "text-[#B8923F]" : "text-slate-400"}`} />
                  <p className="font-extrabold text-[14px] text-slate-900">FREE Resume Evaluation</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">Attach your resume — an expert reviews it personally.</p>
                </button>
                <button
                  type="button"
                  onClick={() => { setTab("call"); setError(null); }}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${
                    tab === "call" ? "border-[#C9A84C] bg-[#C9A84C]/10 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <PhoneCall className={`w-5 h-5 mb-2 ${tab === "call" ? "text-[#B8923F]" : "text-slate-400"}`} />
                  <p className="font-extrabold text-[14px] text-slate-900">FREE 1-on-1 Strategy Call</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">Talk to a career expert — or have it all done FOR you.</p>
                </button>
              </div>

              {tab === "call" ? (
                /* Strategy call = book straight on Marcus's Calendly. No form,
                   no back-and-forth — pick a slot and it's locked in. */
                <div className="text-center py-4">
                  <PhoneCall className="w-10 h-10 mx-auto text-[#B8923F] mb-3" />
                  <p className="text-[18px] font-extrabold text-slate-900">
                    Book your FREE 1-on-1 call — live on the calendar
                  </p>
                  <p className="mt-2 text-[14px] text-slate-500 max-w-md mx-auto">
                    Talk to a career expert about your search, see TailorWiz live,
                    or find out what it looks like to have your job search done FOR you.
                    Pick any open slot — it's locked in instantly.
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#E5C36A] text-[#0B1C3F] font-extrabold py-3.5 px-10 rounded-full text-[15px] transition-all shadow-md"
                  >
                    <PhoneCall className="w-4 h-4" /> Pick My Time on the Calendar
                  </a>
                  <p className="mt-3 text-[12px] text-slate-400">
                    No cost, no obligation. 1-on-1 with a real career expert.
                  </p>
                </div>
              ) : (
              <>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[12px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Your name *</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[12px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Email *</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[12px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Phone (optional)</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[12px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Target role (optional)</label>
                  <input value={targetRole} onChange={(e) => setTargetRole(e.target.value)} placeholder="e.g. Project Manager" className={inputCls} />
                </div>
              </div>

                  <label
                    className={`block rounded-xl border-2 border-dashed px-5 py-6 text-center cursor-pointer transition-colors mb-4 ${
                      file ? "border-emerald-400 bg-emerald-50/50" : "border-slate-300 bg-slate-50 hover:border-[#C9A84C]"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.rtf"
                      className="hidden"
                      onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                    />
                    {file ? (
                      <span className="inline-flex items-center gap-2 text-[14px] font-bold text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" /> {file.name} attached — click to change
                      </span>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 mx-auto text-[#B8923F]" />
                        <p className="mt-2 text-[14px] font-bold text-slate-900">Attach your resume *</p>
                        <p className="text-[12px] text-slate-500">PDF or Word, up to 5MB · 100% private, never shared</p>
                      </>
                    )}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Anything we should know? (industry, career change, how long you've been searching…)"
                    className={`${inputCls} resize-none mb-4`}
                  />

              {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[13px] text-red-700 mb-4">{error}</div>}

              <button
                onClick={() => void submit()}
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#E5C36A] disabled:opacity-50 text-[#0B1C3F] font-extrabold py-3.5 rounded-full text-[15px] transition-all shadow-md"
              >
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send My Resume for FREE Evaluation</>}
              </button>
              <p className="mt-3 text-[12px] text-slate-400 text-center">
                No cost, no obligation. A real person replies — not a bot.
              </p>
              </>
              )}
            </div>
          )}
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
